import { useState } from 'react';
import { useProjectStore } from '../../stores/useProjectStore';
import { FileTree } from './FileTree';
import { Plus, Trash2, FolderPlus } from 'lucide-react';
import styles from './FileExplorer.module.css';

export function FileExplorer() {
  const projects = useProjectStore(s => s.projects);
  const activeProjectId = useProjectStore(s => s.activeProjectId);
  const activeProject = useProjectStore(s => s.activeProject);
  const openProject = useProjectStore(s => s.openProject);
  const deleteProject = useProjectStore(s => s.deleteProject);
  const closeProject = useProjectStore(s => s.closeProject);
  const addFile = useProjectStore(s => s.addFile);
  const openFile = useProjectStore(s => s.openFile);
  const deleteFile = useProjectStore(s => s.deleteFile);

  const [newFileName, setNewFileName] = useState('');
  const [showNewFile, setShowNewFile] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const handleCreateFile = async () => {
    const name = newFileName.trim();
    if (!name) return;
    // .mist files get a starter template
    const template = name.endsWith('.mist')
      ? `contract ${name.replace(/\.mist$/, '').replace(/[^a-zA-Z0-9_]/g, '')}\n    state owner: Address\n\n    function init()\n        owner = caller\n`
      : '';
    await addFile(name, template);
    setNewFileName('');
    setShowNewFile(false);
  };

  const handleDeleteProject = async (id: string) => {
    await deleteProject(id);
    setConfirmDelete(null);
  };

  return (
    <div className={styles.explorer}>
      {/* Project List */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionLabel}>Projects</span>
        </div>
        <div className={styles.projectList}>
          {projects.length === 0 && (
            <div className={styles.emptyState}>
              No projects yet
            </div>
          )}
          {projects.map(p => (
            <div key={p.id} className={styles.projectRow}>
              <button
                className={`${styles.projectItem} ${activeProjectId === p.id ? styles.activeProject : ''}`}
                onClick={() => openProject(p.id)}
                title={p.name}
              >
                <FolderPlus size={14} className={styles.projectIcon} />
                <span className={styles.projectName}>{p.name}</span>
                <span className={styles.fileCount}>{p.fileCount}</span>
              </button>
              {activeProjectId === p.id && (
                <div className={styles.projectActions}>
                  {confirmDelete === p.id ? (
                    <>
                      <button className={styles.dangerSmall} onClick={() => handleDeleteProject(p.id)} title="Confirm delete">
                        Delete
                      </button>
                      <button className={styles.cancelSmall} onClick={() => setConfirmDelete(null)} title="Cancel">
                        &#x2715;
                      </button>
                    </>
                  ) : (
                    <button className={styles.iconBtn} onClick={() => setConfirmDelete(p.id)} title="Delete project">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* File Tree for Active Project */}
      {activeProject && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{activeProject.name}</span>
            <div className={styles.sectionActions}>
              <button className={styles.iconBtn} onClick={() => setShowNewFile(true)} title="New file">
                <Plus size={14} />
              </button>
              <button className={styles.iconBtn} onClick={closeProject} title="Close project">
                &#x2715;
              </button>
            </div>
          </div>

          {showNewFile && (
            <div className={styles.newFileRow}>
              <input
                className={styles.newFileInput}
                value={newFileName}
                onChange={e => setNewFileName(e.target.value)}
                placeholder="filename.mist"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCreateFile();
                  if (e.key === 'Escape') { setShowNewFile(false); setNewFileName(''); }
                }}
              />
              <button className={styles.iconBtn} onClick={handleCreateFile} title="Create">
                &#x2713;
              </button>
            </div>
          )}

          <FileTree
            files={activeProject.files}
            onOpenFile={openFile}
            onDeleteFile={deleteFile}
          />

          {activeProject.files.length === 0 && !showNewFile && (
            <div className={styles.emptyState}>
              No files — click + to create one
            </div>
          )}
        </div>
      )}
    </div>
  );
}
